import { useState } from 'react';
import { TRACK_GUIDE } from '../data/trackGuide';

const TYPE_COLOUR = {
  'Hairpin':      '#ef4444',
  'Tight Hairpin':'#ef4444',
  'Chicane':      '#f97316',
  'Complex':      '#a78bfa',
  'Fast':         '#22c55e',
  'Ultra-Fast':   '#4ade80',
  'Medium':       '#f5c518',
  'Full Throttle':'#38bdf8',
  'Long':         '#e8002d',
};
function cornerColour(type='') {
  for (const [k,v] of Object.entries(TYPE_COLOUR)) if (type.includes(k)) return v;
  return '#8ba3cc';
}

function GearDots({ gear }) {
  return (
    <div style={{display:'flex',gap:3,alignItems:'center'}}>
      {Array.from({length:8}).map((_,i) => (
        <div key={i} style={{width:8,height:8,borderRadius:'50%',
          background: i < gear ? '#e8002d' : 'var(--border,#1e3a6e)'}}/>
      ))}
      <span style={{fontSize:'0.62rem',color:'var(--text,#e8edf8)',marginLeft:4,fontFamily:"'Bebas Neue',sans-serif",fontSize:'1rem'}}>{gear}</span>
    </div>
  );
}

function RacingLineSVG({ type }) {
  // Simple schematic of racing line for the corner type
  const isLeft  = type.includes('Left');
  const isRight = !isLeft || type.includes('Right');
  const isFast  = type.includes('Fast') || type.includes('Ultra');
  const isHairpin = type.includes('Hairpin');
  const isChicane = type.includes('Chicane') || type.includes('Complex') || type.includes('S-');

  let d = '';
  if (isHairpin) {
    d = isLeft
      ? 'M10,10 L10,50 Q10,90 50,90 Q90,90 90,50 L90,10'
      : 'M90,10 L90,50 Q90,90 50,90 Q10,90 10,50 L10,10';
  } else if (isChicane) {
    d = 'M10,80 Q30,80 40,50 Q50,20 60,20 Q70,20 90,20';
  } else if (isFast) {
    d = isLeft
      ? 'M90,10 Q90,30 70,50 Q50,70 10,80'
      : 'M10,10 Q10,30 30,50 Q50,70 90,80';
  } else {
    d = isLeft
      ? 'M90,10 Q80,20 60,40 Q30,70 10,80'
      : 'M10,10 Q20,20 40,40 Q70,70 90,80';
  }

  return (
    <svg viewBox="0 0 100 100" style={{width:80,height:80}}>
      {/* Track edges */}
      <rect x="2" y="2" width="96" height="96" rx="4" fill="#0a1628" stroke="var(--border,#1e3a6e)" strokeWidth="1"/>
      {/* Ideal racing line */}
      <path d={d} fill="none" stroke="rgba(232,0,45,0.3)" strokeWidth="18" strokeLinecap="round"/>
      <path d={d} fill="none" stroke="#e8002d" strokeWidth="3" strokeLinecap="round" strokeDasharray="4 3"/>
      {/* Entry arrow */}
      <circle cx={isLeft?90:10} cy={10} r={4} fill="#22c55e"/>
      {/* Exit arrow */}
      <circle cx={isLeft?10:90} cy={80} r={4} fill="#38bdf8"/>
    </svg>
  );
}

export default function TrackGuide({ circuitName, onBack }) {
  const [cornerIdx, setCornerIdx] = useState(0);
  const [imgError, setImgError]   = useState({});
  const guide = TRACK_GUIDE[circuitName];

  if (!guide) return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Back</button>
        <div className="g-title" style={{color:'#e8002d'}}>TRACK GUIDE</div>
      </div>
      <div style={{textAlign:'center',padding:40}}>
        <p style={{color:'var(--sub,#8ba3cc)'}}>No guide data for {circuitName} yet</p>
      </div>
    </div>
  );

  const corners  = guide.cornerList;
  const hasCorners = corners.length > 0;
  const corner   = hasCorners ? corners[cornerIdx] : null;

  const prev = () => setCornerIdx(i => Math.max(0, i-1));
  const next = () => setCornerIdx(i => Math.min(corners.length-1, i+1));

  return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Back</button>
        <div className="g-title" style={{color:'#e8002d'}}>{circuitName?.toUpperCase()} GUIDE</div>
        {hasCorners && (
          <span style={{fontSize:'0.6rem',color:'#e8002d'}}>{cornerIdx+1}/{corners.length}</span>
        )}
      </div>

      <div className="f1-live-wrap" style={{paddingBottom:40}}>

        {/* Circuit stats bar */}
        <div style={{display:'flex',gap:8,flexWrap:'wrap',background:'var(--surface,#0f1e38)',
          borderRadius:10,padding:'10px 14px',border:'1px solid var(--border,#1e3a6e)'}}>
          {[
            {lbl:'Length',   val:guide.length},
            {lbl:'Corners',  val:guide.corners},
            {lbl:'Lap Record',val:guide.lapRecord},
          ].map(({lbl,val}) => (
            <div key={lbl} style={{display:'flex',flexDirection:'column',gap:1,flex:1,minWidth:80}}>
              <span style={{fontSize:'0.48rem',letterSpacing:'0.15em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase'}}>{lbl}</span>
              <span style={{fontSize:'0.72rem',fontWeight:700,color:'var(--text,#e8edf8)'}}>{val}</span>
            </div>
          ))}
        </div>

        {/* Character blurb */}
        {guide.character && (
          <p style={{fontSize:'0.76rem',color:'var(--sub,#8ba3cc)',lineHeight:1.6,
            borderLeft:'3px solid #e8002d',paddingLeft:10}}>
            {guide.character}
          </p>
        )}

        {/* Corner navigator */}
        {hasCorners ? (<>

          {/* Corner dots progress */}
          <div style={{display:'flex',gap:4,flexWrap:'wrap',justifyContent:'center'}}>
            {corners.map((c,i) => (
              <button key={i} onClick={()=>setCornerIdx(i)}
                style={{width:i===cornerIdx?24:10,height:10,borderRadius:5,border:'none',cursor:'pointer',
                  background:i===cornerIdx?'#e8002d':cornerColour(c.type),
                  opacity:i===cornerIdx?1:0.4,transition:'all 0.2s'}}
                title={`T${c.n} ${c.name}`}/>
            ))}
          </div>

          {/* Corner header */}
          <div style={{background:'rgba(232,0,45,0.06)',border:'1px solid #e8002d',
            borderRadius:12,padding:'14px 16px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <div>
                <div style={{fontSize:'0.52rem',letterSpacing:'0.15em',color:'#e8002d',textTransform:'uppercase',marginBottom:3}}>
                  Turn {corner.n}
                </div>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.6rem',
                  color:'var(--text,#e8edf8)',letterSpacing:'0.04em',lineHeight:1}}>
                  {corner.name}
                </div>
              </div>
              <span style={{padding:'3px 10px',borderRadius:6,fontSize:'0.62rem',fontWeight:700,
                background:cornerColour(corner.type)+'22',color:cornerColour(corner.type),
                border:`1px solid ${cornerColour(corner.type)}`}}>
                {corner.type}
              </span>
            </div>

            {/* Speed + gear */}
            <div style={{display:'flex',gap:16,alignItems:'center',flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:'0.48rem',letterSpacing:'0.12em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase'}}>Entry Speed</div>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.5rem',color:'#f5c518'}}>{corner.speed} <span style={{fontSize:'0.7rem'}}>km/h</span></div>
              </div>
              <div>
                <div style={{fontSize:'0.48rem',letterSpacing:'0.12em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase',marginBottom:4}}>Gear</div>
                <GearDots gear={corner.gear}/>
              </div>
            </div>
          </div>

          {/* Photo */}
          {corner.photo && !imgError[cornerIdx] && (
            <div style={{borderRadius:12,overflow:'hidden',border:'1px solid var(--border,#1e3a6e)',
              background:'#000',aspectRatio:'16/9',position:'relative'}}>
              <img src={corner.photo} alt={corner.name}
                onError={()=>setImgError(e=>({...e,[cornerIdx]:true}))}
                style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
              <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'6px 10px',
                background:'linear-gradient(transparent,rgba(0,0,0,0.7))',fontSize:'0.58rem',color:'rgba(255,255,255,0.7)'}}>
                {corner.name} · {circuitName}
              </div>
            </div>
          )}

          {/* Racing line schematic + braking */}
          <div style={{display:'grid',gridTemplateColumns:'80px 1fr',gap:12,alignItems:'start'}}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
              <RacingLineSVG type={corner.type}/>
              <span style={{fontSize:'0.5rem',color:'var(--sub,#4a6a9e)',textAlign:'center'}}>Racing line</span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <div style={{background:'var(--surface,#0f1e38)',borderRadius:9,padding:'10px 12px',border:'1px solid var(--border,#1e3a6e)'}}>
                <div style={{fontSize:'0.5rem',letterSpacing:'0.12em',color:'#ef4444',textTransform:'uppercase',marginBottom:4}}>Braking</div>
                <div style={{fontSize:'0.75rem',color:'var(--text,#e8edf8)',lineHeight:1.5}}>{corner.braking}</div>
              </div>
              <div style={{background:'var(--surface,#0f1e38)',borderRadius:9,padding:'10px 12px',border:'1px solid #e8002d'}}>
                <div style={{fontSize:'0.5rem',letterSpacing:'0.12em',color:'#e8002d',textTransform:'uppercase',marginBottom:4}}>Racing Line</div>
                <div style={{fontSize:'0.75rem',color:'var(--text,#e8edf8)',lineHeight:1.5}}>{corner.line}</div>
              </div>
            </div>
          </div>

          {/* Prev / Next navigation */}
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <button onClick={prev} disabled={cornerIdx===0}
              style={{flex:1,padding:'11px',border:'1px solid var(--border,#1e3a6e)',borderRadius:10,
                background:'transparent',color:cornerIdx===0?'var(--border,#1e3a6e)':'var(--text,#e8edf8)',
                cursor:cornerIdx===0?'not-allowed':'pointer',fontFamily:'Inter,sans-serif',
                fontSize:'0.84rem',fontWeight:600,transition:'border-color 0.15s'}}
              onMouseEnter={e=>{if(cornerIdx>0)e.currentTarget.style.borderColor='#e8002d';}}
              onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border,#1e3a6e)'}>
              ← {cornerIdx>0?`T${corners[cornerIdx-1].n} ${corners[cornerIdx-1].name}`:'Start'}
            </button>
            <button onClick={next} disabled={cornerIdx===corners.length-1}
              style={{flex:1,padding:'11px',border:'1px solid var(--border,#1e3a6e)',borderRadius:10,
                background:cornerIdx<corners.length-1?'rgba(232,0,45,0.08)':'transparent',
                color:cornerIdx===corners.length-1?'var(--border,#1e3a6e)':'#e8002d',
                cursor:cornerIdx===corners.length-1?'not-allowed':'pointer',fontFamily:'Inter,sans-serif',
                fontSize:'0.84rem',fontWeight:600,borderColor:cornerIdx<corners.length-1?'#e8002d':'var(--border,#1e3a6e)',
                transition:'all 0.15s'}}>
              {cornerIdx<corners.length-1?`T${corners[cornerIdx+1].n} ${corners[cornerIdx+1].name}`:'Finish'} →
            </button>
          </div>

        </>) : (
          <div style={{textAlign:'center',padding:32}}>
            <div style={{fontSize:'2rem',marginBottom:8}}>🏎️</div>
            <p style={{color:'var(--sub,#8ba3cc)',fontSize:'0.84rem'}}>Detailed corner guide coming soon for {circuitName}</p>
            <p style={{color:'var(--sub,#4a6a9e)',fontSize:'0.7rem',marginTop:6,lineHeight:1.6}}>
              Full corner-by-corner breakdown with racing lines,<br/>
              braking zones and photos available for:<br/>
              <strong style={{color:'var(--text,#e8edf8)'}}>Silverstone · Monaco · Spa · Monza · Suzuka</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
